import { Fragment, useMemo } from 'react'
import { parseManualMarkdown, type Inline } from '../../utils/manualMarkdown'
import { cn } from '../../lib/shadcn/utils'

function Text({ parts }: { parts: Inline[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.bold ? (
          <strong key={i} className="font-semibold text-foreground">
            {p.text}
          </strong>
        ) : (
          <Fragment key={i}>{p.text}</Fragment>
        ),
      )}
    </>
  )
}

/**
 * Renders a manual section body. Every block becomes a React element built from
 * text; see utils/manualMarkdown.ts for why the subset is fixed and why nothing
 * here touches innerHTML.
 */
export function ManualMarkdown({ body, compact = false }: { body: string; compact?: boolean }) {
  const blocks = useMemo(() => parseManualMarkdown(body), [body])
  const gap = compact ? 'space-y-2' : 'space-y-3'
  return (
    <div className={cn('text-sm leading-relaxed text-foreground/90', gap)}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'heading':
            return (
              <p key={i} className="pt-1 font-semibold text-foreground">
                <Text parts={b.text} />
              </p>
            )
          case 'paragraph':
            return (
              <p key={i}>
                <Text parts={b.text} />
              </p>
            )
          case 'callout':
            return (
              <div key={i} className="rounded-md border-l-4 border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] px-3 py-2 text-[hsl(var(--brand-soft-foreground))]">
                <Text parts={b.text} />
              </div>
            )
          case 'bullets':
            return (
              <ul key={i} className="list-disc space-y-1 pl-5">
                {b.items.map((it, j) => (
                  <li key={j}>
                    <Text parts={it} />
                  </li>
                ))}
              </ul>
            )
          case 'numbered':
            return (
              <ol key={i} start={b.start} className="list-decimal space-y-1 pl-6">
                {b.items.map((it, j) => (
                  <li key={j}>
                    <Text parts={it} />
                  </li>
                ))}
              </ol>
            )
          case 'table':
            return (
              <div key={i} className="max-w-full overflow-x-auto rounded-md border border-border">
                <table className="w-full border-collapse text-left text-xs">
                  <thead className="bg-muted/60">
                    <tr>
                      {b.header.map((c, j) => (
                        <th key={j} className="border-b border-border px-2 py-1.5 font-semibold text-foreground">
                          <Text parts={c} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j} className="align-top odd:bg-background even:bg-muted/20">
                        {r.map((c, k) => (
                          <td key={k} className="border-b border-border px-2 py-1.5">
                            <Text parts={c} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        }
      })}
    </div>
  )
}
